import { useCustomSession } from "shared/context/CustomSessionContext";
import { resolveDeviceInfoVisibility } from "shared/helpers/device-info/resolveDeviceInfoVisibility";
import { useCurrentRouteConfig } from "hub/src/context/CurrentRouteConfigContext";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";

type UseBackButtonVisibilityProps = {
  /** What the page itself asked for through `PageComponent.Header`. */
  showBackButton?: boolean;
};

/**
 * Whether the page header shows its back button in place of the menu button.
 *
 * - A page that asks for one (`showBackButton={true}`) always gets it: those
 *   pages need it to navigate, such as an open conference event or a chat.
 * - Otherwise the route's `platformConfig.backButton` decides, falling back key
 *   by key to the society's `hubDefaultPlatformConfig.backButton`.
 * - When neither decides for this device, or the page is not a configured
 *   route (login, access denied, …), the page's own value stands.
 */
export const useBackButtonVisibility = ({
  showBackButton,
}: UseBackButtonVisibilityProps): boolean => {
  const routeConfig = useCurrentRouteConfig();
  const session = useCustomSession();
  const device = useDeviceInfo({ watchScreen: true });

  if (showBackButton || !routeConfig) {
    return showBackButton ?? false;
  }

  return (
    resolveDeviceInfoVisibility({
      device,
      config: routeConfig.platformConfig?.backButton,
      defaultConfig:
        session.society?.societySettingsPublic?.hubDefaultPlatformConfig
          ?.backButton,
    }) ??
    showBackButton ??
    false
  );
};
